import { useCanOpen } from '@/hooks/useCanOpen';
import { usePendingCount } from '@/hooks/useWorkflows';
import { useUnreadCount } from '@/hooks/useMessages';
import { useCountPendingAbsences } from '@/hooks/useEmployeeAbsences';
import { useSubmittedTimeEntriesCount } from '@/hooks/useTimeTracking';
import { useOverdueInvoiceCount } from '@/hooks/useDashboard';

export interface NavBadge {
  /** `undefined` while loading, after a failed read, or when disabled: show no badge, never "0". */
  count: number | undefined;
  /** The session may open the page the badge sits on, so the count is fetched. */
  enabled: boolean;
}

export interface NavBadges {
  approvals: NavBadge;
  messages: NavBadge;
  absences: NavBadge;
  timeEntries: NavBadge;
  overdueInvoices: NavBadge;
}

/**
 * The counts behind the navigation badges. Each count runs only when the user
 * may open the page it badges, so an employee without the module issues no
 * request for it. All of them poll every minute while the tab is visible and
 * refresh when it becomes visible again (`visiblePoll`, set in the underlying hooks).
 */
export function useNavBadges(): NavBadges {
  const canOpen = useCanOpen();

  const approvalsEnabled = canOpen('/workflows');
  const messagesEnabled = canOpen('/messaging');
  const absencesEnabled = canOpen('/shifts');
  const timeEntriesEnabled = canOpen('/time-tracking');
  const overdueInvoicesEnabled = canOpen('/billing');

  const approvals = usePendingCount({ enabled: approvalsEnabled });
  const messages = useUnreadCount('employee', { enabled: messagesEnabled, audience: 'all' });
  const absences = useCountPendingAbsences({ forMe: true, enabled: absencesEnabled });
  const timeEntries = useSubmittedTimeEntriesCount({ enabled: timeEntriesEnabled });
  const overdueInvoices = useOverdueInvoiceCount({ enabled: overdueInvoicesEnabled });

  return {
    approvals: badge(approvalsEnabled, approvals.isSuccess ? approvals.data?.count : undefined),
    messages: badge(messagesEnabled, messages.isSuccess ? messages.data?.unread_count : undefined),
    absences: badge(absencesEnabled, absences.isSuccess ? absences.data?.count : undefined),
    timeEntries: badge(timeEntriesEnabled, timeEntries.isSuccess ? timeEntries.data : undefined),
    overdueInvoices: badge(
      overdueInvoicesEnabled,
      overdueInvoices.isSuccess ? overdueInvoices.data : undefined,
    ),
  };
}

function badge(enabled: boolean, count: number | undefined): NavBadge {
  return { enabled, count: enabled ? count : undefined };
}
